import { createElement, forwardRef } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";
import { BookOpenText, Briefcase, ChartLine, FileText, Home, Inbox, LibraryBig, ListChecks, MessageSquareText, Radar } from "lucide-react";
import { PlatformIcon, type SpaceInfo, type SpaceText, type WorkspaceSummary } from "@ncp/assistant-sdk";

/** Permission keys mirror `Ncp.Application/Platform/Permissions.cs`. */
export const P = {
  use: "assistant.use",
  attach: "assistant.attach",
  delegate: "assistant.delegate",
  confirm: "assistant.confirm",
  artifactsExport: "assistant.artifacts.export",
  knowledgeRead: "knowledge.read",
  knowledgeUpload: "knowledge.upload",
  knowledgeManage: "knowledge.manage",
  mattersRead: "matters.read",
  mattersCreate: "matters.create",
  mattersWork: "matters.work",
  watchers: "watchers.use",
  economicsRead: "economics.read",
} as const;

/** The sidebar's groups: the day's work, the workspace's own analysis pages, and what the work draws on or produces. */
export type NavSection = "work" | "insight" | "resources";
export const NAV_SECTIONS: readonly NavSection[] = ["work", "insight", "resources"];

export interface NavEntry {
  key: string;
  /** i18n key under `assistant:nav.*` (a workspace vocabulary may reword it under `assistant:vocab.<name>.nav.*`). */
  label: string;
  /** A label already in the reader's language (a page's title, or the space manifest's own label); shown instead of `label`. */
  text?: string;
  icon: LucideIcon;
  href: string;
  /** Required permission; `null` means any workspace member. */
  permission: string | null;
  /** Letter for the `g <letter>` shortcut. */
  shortcut?: string;
  section: NavSection;
}

export const NAV: NavEntry[] = [
  { key: "home", label: "home", icon: Home, href: "/", permission: null, shortcut: "h", section: "work" },
  { key: "chat", label: "chat", icon: MessageSquareText, href: "/chat", permission: null, shortcut: "c", section: "work" },
  { key: "matters", label: "matters", icon: Briefcase, href: "/matters", permission: P.mattersRead, shortcut: "m", section: "work" },
  { key: "inbox", label: "inbox", icon: Inbox, href: "/inbox", permission: null, shortcut: "i", section: "work" },
  { key: "knowledge", label: "knowledge", icon: BookOpenText, href: "/knowledge", permission: P.knowledgeRead, shortcut: "k", section: "resources" },
  { key: "watchers", label: "watchers", icon: Radar, href: "/watchers", permission: P.watchers, shortcut: "w", section: "resources" },
  { key: "artifacts", label: "artifacts", icon: FileText, href: "/artifacts", permission: null, shortcut: "a", section: "resources" },
];

/** Built-in screens beyond the standard navigation, shown when a space's manifest names them; keys mirror `SpaceBuiltins.Nav` in the API. */
export const BUILTIN_PAGES: Record<string, NavEntry> = {
  evidence: { key: "evidence", label: "evidence", icon: ChartLine, href: "/evidence", permission: P.economicsRead, shortcut: "e", section: "insight" },
  tracker: { key: "tracker", label: "tracker", icon: ListChecks, href: "/tracker", permission: P.mattersRead, shortcut: "t", section: "insight" },
  library: { key: "library", label: "library", icon: LibraryBig, href: "/library", permission: P.knowledgeRead, shortcut: "l", section: "insight" },
};

const BUILTIN: Record<string, NavEntry> = { ...Object.fromEntries(NAV.map((e) => [e.key, e])), ...BUILTIN_PAGES };

export const spaceText = (text: SpaceText | null | undefined, locale: string): string | undefined =>
  text ? (locale === "ar" && text.ar ? text.ar : text.en) : undefined;

/** The page's title in the reader's language, from the space's published pages. */
export function pageTitle(space: SpaceInfo | null | undefined, page: string, locale: string): string {
  const p = space?.pages.find((x) => x.key === page);
  return p ? (locale === "ar" && p.nameAr ? p.nameAr : p.name) : page;
}

const icons = new Map<string, LucideIcon>();
/** A Lucide icon by name, as a component like the built-in ones (cached, so it keeps its identity across renders). */
export const iconFor = (name: string): LucideIcon => {
  let Icon = icons.get(name);
  if (!Icon) {
    Icon = forwardRef<SVGSVGElement, LucideProps>((props, _ref) => createElement(PlatformIcon, { ...props, name }));
    Icon.displayName = `NavIcon(${name})`;
    icons.set(name, Icon);
  }
  return Icon;
};

/** A space whose manifest lists its navigation (every workspace's does once the API has given it one). */
export const ownsNav = (space: SpaceInfo | null | undefined): space is SpaceInfo => !!space && space.nav.length > 0;

/**
 * The workspace's navigation: the screens and pages its space manifest lists, section by section (Home always comes first), or the
 * standard navigation for a space that lists none.
 */
export function navFor(workspace: Pick<WorkspaceSummary, "space"> | null | undefined, locale: string): NavEntry[] {
  const space = workspace?.space;
  if (ownsNav(space)) {
    const entries: NavEntry[] = [];
    for (const n of space.nav) {
      const section = (NAV_SECTIONS as readonly string[]).includes(n.section) ? (n.section as NavSection) : "work";
      const text = spaceText(n.label, locale);
      if (n.page) {
        const page = space.pages.find((p) => p.key === n.page);
        entries.push({
          key: `page:${n.key}`, label: n.key, text: text ?? pageTitle(space, n.page, locale), icon: iconFor(n.icon ?? page?.icon ?? "LayoutDashboard"),
          href: `/p/${encodeURIComponent(n.page)}`, permission: n.permission ?? null, section,
        });
      } else {
        const base = BUILTIN[n.builtin ?? n.key];
        if (base) entries.push({ ...base, key: base.key, text, section, permission: n.permission ?? base.permission, icon: n.icon ? iconFor(n.icon) : base.icon });
      }
    }
    if (!entries.some((e) => e.key === "home")) entries.unshift(NAV[0]!);
    return NAV_SECTIONS.flatMap((s) => entries.filter((e) => e.section === s));
  }
  return NAV_SECTIONS.flatMap((s) => NAV.filter((e) => e.section === s));
}

/** Whether the workspace's navigation shows a built-in screen (Evidence, Tracker, Library). */
export function showsBuiltin(workspace: Pick<WorkspaceSummary, "space"> | null | undefined, key: string): boolean {
  const space = workspace?.space;
  return ownsNav(space) && space.nav.some((n) => !n.page && (n.builtin ?? n.key) === key);
}
